/**
 * Boot-time fog-memory seeding (HEX-84 / HEX-73).
 *
 * A character persists two fog sets: `explored` (hexes personally crossed) and
 * `revealed` (hexes a bought map showed). On a fresh boot the session memory set
 * (`discovered`, which drives the desaturated memory face in `refreshVision`)
 * starts empty and only fills from the live vision disk as the character walks —
 * so without an explicit re-seed every previously-seen tile vanishes on refresh.
 *
 * This module is the pure-ish core: fold a set of `q,r` keys into the memory set
 * and render their tiles. The session injects its world store + renderer through
 * `MemorySeedTarget`, keeping the kernel unit-testable without Pixi or a DOM.
 */

import { type Hex, parseHexKey } from '../core/hex';
import type { Tile } from '../core/tile';

/** Collaborators a memory seed needs, injected by the game session. */
export interface MemorySeedTarget {
  /** Warm the chunk containing `hex` so its tile is materialised (async). */
  ensureChunk(hex: Hex): Promise<unknown>;
  /** Materialised tile at `hex`, or `null` if absent. */
  getTileAt(hex: Hex): Tile | null;
  /** Render `tile` at `hex` — only called for not-yet-rendered keys. */
  addHex(hex: Hex, tile: Tile): void;
  /** Session memory set — seeded keys are added here. */
  discovered: Set<string>;
  /** Already-rendered keys — skipped for re-render, extended when newly rendered. */
  rendered: Set<string>;
  /** True once the session is torn down; aborts the seed mid-loop. */
  isDestroyed(): boolean;
}

/**
 * Fold `keys` into the session memory set and render any not-yet-rendered tiles,
 * so a character's persisted fog paints from session start. Mirrors the same
 * warm → record → render step used per-move; shared by the explored (HEX-84) and
 * revealed (HEX-73) boot seeds so the two paths cannot drift.
 */
export async function seedMemory(
  keys: ReadonlySet<string>,
  target: MemorySeedTarget,
): Promise<void> {
  if (keys.size === 0) return;
  for (const key of keys) {
    const hex = parseHexKey(key);
    if (!hex) continue;
    await target.ensureChunk(hex);
    if (target.isDestroyed()) return;
    target.discovered.add(key);
    if (target.rendered.has(key)) continue;
    const tile = target.getTileAt(hex);
    if (tile) {
      target.addHex(hex, tile);
      target.rendered.add(key);
    }
  }
}
